/* eslint-disable no-nested-ternary */
import { ChangeEvent, FC, useState, FocusEvent } from 'react';
import { TextField, Theme } from '@mui/material';
import { validateEmail } from 'utils';
import { UsernameOrEmailProps } from './usernameOrEmailTypes';
import { IconButton, InputAdornment, CheckIcon, createStyles, makeStyles } from '../uiLibraryImports';
import { useTranslationContext } from '../contexts/translationContext';
import './usernameOrEmail.scss';

const UsernameOrEmail: FC<UsernameOrEmailProps> = (props: UsernameOrEmailProps) => {
    const {
        isValidUsername,
        usernameOrEmail,
        onChange,
        onChangeWithValidate,
        onlyEmail,
        error,
        readOnly,
        placeholder,
        emailPending,
        errorMessage,
        onError,
        correctEmail,
        correctEmailDisabled,
        disabled,
        autofocus,
        onBlurFunction,
        autoComplete = 'on',
        color = 'white',
        id,
        outerHelperText
    } = props;
    const { translate } = useTranslationContext();

    const [emailError, setEmailError] = useState<boolean>(false);
    const [helperText, setHelperText] = useState<string>('');
    const [text, setText] = useState<string>(usernameOrEmail);

    const invalidEmail = translate('InvalidEmail');

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const { value } = e.target;
        setText(value);
        onChange?.(value);

        if (onChangeWithValidate) {
            const isValidEmail = validateEmail(value);
            onChangeWithValidate({ value, isValidEmail });
            if (isValidEmail) {
                setEmailError(false);
                setHelperText('');
            }
        }
    };

    const onBlur = () => {
        if ((onlyEmail !== undefined && onlyEmail) || (correctEmail !== undefined && correctEmail)) {
            const validEmail = validateEmail(text);
            if (text && !validEmail) {
                setHelperText(invalidEmail);
                setEmailError(true);
                onError?.(true);
            } else {
                setEmailError(false);
                setHelperText('');
                onError?.(false);
            }
        }
        onBlurFunction?.(text);
    };

    const useStyles = makeStyles((_: Theme) =>
        createStyles({
            cssOutlinedInput: {
                height: '2.75rem !important',
                width: '100% !important',
                background: 'rgba(38, 35, 58, 0.5)',
                color,
                '&$cssFocused $notchedOutline': {
                    borderColor: 'rgba(71, 154, 235, 1) !important'
                }
            },
            cssCorrectEmail: {
                height: '2.75rem !important',
                width: '100% !important',
                color
            },
            cssReadOnly: {
                height: '2.75rem !important',
                width: '100% !important',
                color
            },
            cssPendingEmail: {
                height: '2.75rem !important',
                width: '100% !important',
                background: 'rgba(236, 140, 181, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)!important',
                fontFamily: 'Inter !important',
                fontStyle: 'normal !important',
                fontSize: '16px !important',
                lineHeight: '140% !important'
            },
            notchedOutlinecorrectEmail: {
                border: '2px solid #EC8CB5 !important'
            },
            notchedOutlineReadOnly: {
                border: 'none'
            },
            notchedOutlinecorrectEmailDisabled: {
                border: '1px solid rgba(255, 255, 255, 0.5) !important'
            },
            cssFocused: {},
            notchedOutline: {
                borderColor:
                    error || emailError
                        ? 'rgba(241, 96, 93, 1) !important'
                        : isValidUsername
                        ? 'rgba(71, 154, 235, 1) !important'
                        : 'rgba(255, 255, 255, 0.25) !important'
            },
            helperText: {
                textAlign: 'right',
                marginRight: 0,
                color: error ? 'rgba(241, 96, 93, 1) !important' : 'rgba(255, 255, 255, 0.50) !important',
                marginLeft: '0 !important'
            }
        })
    );
    const classes = useStyles();

    const rootClassName: string = readOnly
        ? 'cssReadOnly'
        : emailPending
        ? 'cssPendingEmail'
        : correctEmail || correctEmailDisabled
        ? 'cssCorrectEmail'
        : 'cssOutlinedInput';
    const rootClasses = classes[rootClassName];

    const notchedOutlineClassName: string =
        readOnly || emailPending
            ? 'notchedOutlineReadOnly'
            : correctEmail
            ? 'notchedOutlinecorrectEmail'
            : correctEmailDisabled
            ? 'notchedOutlinecorrectEmailDisabled'
            : 'notchedOutline';

    const notchedOutlineClasses = classes[notchedOutlineClassName];
    const defaultClass = !readOnly && !emailPending && !correctEmail;

    return (
        <TextField
            id={id}
            className="usernameOrEmail"
            type="text"
            value={text}
            autoFocus={autofocus}
            helperText={helperText || errorMessage || outerHelperText}
            onChange={handleOnChange}
            FormHelperTextProps={{
                className: classes.helperText
            }}
            placeholder={placeholder}
            disabled={disabled || readOnly || emailPending}
            onBlur={(_: FocusEvent<HTMLInputElement>): void => onBlur()}
            variant="outlined"
            inputProps={{
                maxLength: 70
            }}
            InputProps={{
                autoComplete,
                classes: {
                    root: rootClasses,
                    ...((correctEmail || defaultClass) && { focused: classes.cssFocused }),
                    notchedOutline: notchedOutlineClasses
                },
                endAdornment: isValidUsername ? (
                    <InputAdornment position="end">
                        <IconButton aria-label="toggle password visibility" edge="end" size="medium">
                            <CheckIcon />
                        </IconButton>
                    </InputAdornment>
                ) : undefined
            }}
        />
    );
};

export default UsernameOrEmail;
